import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { supportedLanguages } from "../translations";

const navItems = (t) => [
  { to: "/", label: t.nav.home, testId: "nav-home" },
  { to: "/ikel-mt", label: t.nav.ikel, testId: "nav-ikel-mt" },
  { to: "/furniture", label: t.nav.furniture, testId: "nav-furniture" },
  { to: "/logistics", label: t.nav.logistics, testId: "nav-logistics" },
];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const close = () => setOpen(false);

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]"
      data-testid="site-navbar"
    >
      <div className="pgb-container flex items-center justify-between h-[104px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link" onClick={close}>
          <img
            src="/brand/planet-group-baku-logo.png"
            alt="Planet Group Baku"
            className="h-14 md:h-20 w-auto object-contain drop-shadow-[0_2px_8px_rgba(11,27,41,0.12)] transition-transform duration-300 group-hover:scale-[1.04]"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {navItems(t).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `nav-link text-[13px] tracking-wider uppercase font-medium transition-colors ${
                  isActive ? "text-[#1D4ED8]" : "text-[#0B1B29] hover:text-[#1D4ED8]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          {/* Language switcher */}
          <div
            className="flex items-center bg-[#F5F5F7] border border-[#E2E8F0] rounded-full overflow-hidden"
            data-testid="lang-switcher"
          >
            {supportedLanguages.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLang(code)}
                data-testid={`lang-toggle-${code}`}
                className={`text-[11px] font-bold tracking-widest uppercase px-2.5 py-1.5 transition-colors ${
                  lang === code
                    ? "bg-[#0B1B29] text-white"
                    : "text-[#536878] hover:text-[#0B1B29]"
                }`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          <Link
            to="/quote"
            data-testid="nav-cta-quote"
            className="hidden sm:inline-flex btn-accent"
          >
            {t.common.requestQuote}
          </Link>

          <button
            type="button"
            className="lg:hidden text-[#0B1B29]"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white" data-testid="mobile-menu">
          <div className="pgb-container py-6 flex flex-col gap-5">
            {navItems(t).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={close}
                data-testid={`${item.testId}-mobile`}
                className={({ isActive }) =>
                  `text-sm tracking-wider uppercase font-medium ${
                    isActive ? "text-[#1D4ED8]" : "text-[#0B1B29]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              onClick={close}
              data-testid="nav-cta-quote-mobile"
              className="btn-accent justify-center"
            >
              {t.common.requestQuote}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
